import React, { useCallback, useEffect, useRef } from "react";

/**
 * Keyboard and ARIA for an `OverflowMenu` inside a product-local sizing
 * wrapper. `OverflowMenu` is shared and not edited (D8), so this works on the
 * DOM it renders.
 *
 * Why it's needed: every OverflowMenu group starts with a label or a divider
 * `<li>` (OverflowMenu.tsx:127-131), so MUI's MenuList picks that `<li>` as the
 * item to auto-focus (MenuList.js activeItemIndex). An `<li>` can't take focus,
 * so the Modal's focus trap focuses the Paper instead. The arrow keys are
 * handled on the list, below the Paper, so they never reach Settings, Pause,
 * Edit or Delete. The trigger also says nothing about the popup.
 *
 * What it does:
 *   - the trigger gets `aria-haspopup="menu"` and `aria-expanded`;
 *   - once the menu has opened (the next frame), focus moves to its first
 *     item, so MUI's own arrow, Home/End and Tab handling works. On a mouse
 *     click the item is not `:focus-visible`, so nothing looks different;
 *   - `aria-expanded` returns to false when the menu's root leaves the DOM
 *     (MUI unmounts it after the exit transition).
 *
 * Usage:
 *   const trigger = useMenuTrigger();
 *   <div ref={trigger.ref} onClickCapture={trigger.onClickCapture} className="…sizing…">
 *     <OverflowMenu … />
 *   </div>
 *
 * The capture handler checks the click's target: React events bubble through
 * portals, so clicks on the open menu's items and backdrop reach this wrapper
 * too and must not re-run it.
 */

const OPEN_MENU = ".MuiMenu-root:not(.MuiModal-hidden)";

/** OverflowMenu's trigger: the wrapper's own button child. */
const triggerIn = (wrapper: HTMLElement | null) => wrapper?.querySelector<HTMLButtonElement>(":scope > button") ?? null;

export const useMenuTrigger = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const watch = useRef<MutationObserver | null>(null);
  const frame = useRef<number | null>(null);

  // Every render: OverflowMenu's button can remount (it renders null with no
  // visible groups). aria-expanded is only seeded, never reset, here: a
  // re-render while the menu is open (the 5s pending poll landing, another
  // page's busy flag) must not mark it closed.
  useEffect(() => {
    const button = triggerIn(ref.current);
    if (!button) return;
    button.setAttribute("aria-haspopup", "menu");
    if (!button.hasAttribute("aria-expanded")) button.setAttribute("aria-expanded", "false");
  });

  useEffect(
    () => () => {
      watch.current?.disconnect();
      if (frame.current != null) window.cancelAnimationFrame(frame.current);
    },
    [],
  );

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    const button = triggerIn(ref.current);
    if (!button || !button.contains(event.target as Node)) return;
    if (frame.current != null) window.cancelAnimationFrame(frame.current);
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      const menus = document.querySelectorAll<HTMLElement>(OPEN_MENU);
      const menu = menus[menus.length - 1];
      if (!menu) return;
      button.setAttribute("aria-expanded", "true");
      const active = document.activeElement;
      if (!active || active.getAttribute("role") !== "menuitem" || !menu.contains(active)) {
        menu.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
      }
      watch.current?.disconnect();
      const observer = new MutationObserver(() => {
        if (menu.isConnected && !menu.classList.contains("MuiModal-hidden")) return;
        button.setAttribute("aria-expanded", "false");
        observer.disconnect();
      });
      if (menu.parentNode) observer.observe(menu.parentNode, { childList: true });
      observer.observe(menu, { attributes: true, attributeFilter: ["class"] });
      watch.current = observer;
    });
  }, []);

  return { ref, onClickCapture };
};

export default useMenuTrigger;
